import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, isValid, parse } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Select } from "components/ui/Select/Select";
import { ItemFormWrapper } from "features/cdm/components/ItemFormWrapper/ItemFormWrapper";
import {
  COVID_VACCINE_ELSEWHERE_PROMPT,
  EDUCATION_PROVIDED_OPTIONS,
  REFERRAL_DETAILS_OPTIONS,
  REFERRED_TO_DIABETES_PREVENTION_OPTIONS,
  REFERRED_TO_EDUCATION_OPTIONS,
  VACCINE_OPTIONS,
  YES_NO_PATIENT_DECLINED_OPTIONS,
} from "features/cdm/constants/formOptions";
import { ensureAbsoluteUrl } from "features/cdm/utils/ensureAbsoluteUrl";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./PlanAndOutcomeForm.module.css";

const VACCINE_DISABLED_VALUES = ["NO", "DECLINED_BY_PATIENT"];
const VACCINE_DATE_REQUIRED_VALUES = ["YES", "GIVEN_ELSEWHERE"];

const ddmmyyyyToIso = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const parsed = parse(ddmmyyyy, "dd-MM-yyyy", new Date());
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
};

const isoToDdmmyyyy = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const parsed = parse(yyyymmdd, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "dd-MM-yyyy") : "";
};

// PP Ref 55: "Referred to Diabetes Prevention" is required only when the patient
// is registered for Pre-Diabetes, so the schema is built per review.
const formSchema = (isRegisteredForPreDiabetes) =>
  z.object({
    diagnosedWithChronicDisease: z
      .string()
      .min(1, "Diagnosed with Chronic Disease is required"),

    diabetesType2: z.boolean(),
    asthma: z.boolean(),
    copd: z.boolean(),
    heartFailure: z.boolean(),
    atrialFibrillation: z.boolean(),
    ischaemicHeartDisease: z.boolean(),
    stroke: z.boolean(),
    tia: z.boolean(),

    influenzaVaccine: z.string().min(1, "Influenza vaccine is required"),
    influenzaVaccineDate: z.string(),
    pneumococcalVaccine: z.string().min(1, "Pneumococcal vaccine is required"),
    pneumococcalVaccineDate: z.string(),
    covidVaccine: z.string().min(1, "COVID vaccine is required"),
    covidVaccineStatusUpToDate: z
      .string()
      .min(1, "COVID vaccine status up to date is required"),

    // Patient Education (Ref 53-55) — moved here from the Tests & Investigations
    // step so referral/education plan content sits with the Plan & Outcome step.
    educationProvided: z.string().min(1, "Education provided is required"),
    referredToEducation: z
      .string()
      .min(1, "Referred to education is required"),
    referredToDiabetesPrevention: z.string().optional(),
    referralDetails: z.string().optional(),
  }).superRefine((values, context) => {
    if (
      VACCINE_DATE_REQUIRED_VALUES.includes(values.influenzaVaccine) &&
      !values.influenzaVaccineDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Influenza vaccine date is required",
        path: ["influenzaVaccineDate"],
      });
    }

    if (
      VACCINE_DATE_REQUIRED_VALUES.includes(values.pneumococcalVaccine) &&
      !values.pneumococcalVaccineDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pneumococcal vaccine date is required",
        path: ["pneumococcalVaccineDate"],
      });
    }

    if (isRegisteredForPreDiabetes && !values.referredToDiabetesPrevention) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Referred to diabetes prevention is required",
        path: ["referredToDiabetesPrevention"],
      });
    }

    const isReferralSelected =
      values.referredToEducation === "YES" ||
      values.referredToDiabetesPrevention === "YES";

    if (isReferralSelected && !values.referralDetails) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Referral details is required when a referral was made",
        path: ["referralDetails"],
      });
    }
  });

export const PlanAndOutcomeForm = ({
  onBack,
  onContinue,
  data,
  isReview,
  isLaoding,
}) => {
  // PP Ref 55: "Referred to Diabetes Prevention" appears/is required only when
  // the patient is registered for Pre-Diabetes.
  const isRegisteredForPreDiabetes =
    data?.preDiabetes === true ||
    (data?.registrationReasons ?? []).includes("PRE_DIABETES");

  const form = useForm({
    resolver: zodResolver(formSchema(isRegisteredForPreDiabetes)),
    defaultValues: {
      diagnosedWithChronicDisease: data?.diagnosedWithChronicDisease || "",
      diabetesType2: data?.diabetesType2 || false,
      asthma: data?.asthma || false,
      copd: data?.copd || false,
      heartFailure: data?.heartFailure || false,
      atrialFibrillation: data?.atrialFibrillation || false,
      ischaemicHeartDisease: data?.ischaemicHeartDisease || false,
      stroke: data?.stroke || false,
      tia: data?.tia || false,
      influenzaVaccine: data?.influenzaVaccine || "",
      influenzaVaccineDate: data?.influenzaVaccineDate || "",
      pneumococcalVaccine: data?.pneumococcalVaccine || "",
      pneumococcalVaccineDate: data?.pneumococcalVaccineDate || "",
      covidVaccine: data?.covidVaccine || "",
      covidVaccineStatusUpToDate: data?.covidVaccineStatusUpToDate || "",
      educationProvided: data?.educationProvided || "",
      referredToEducation: data?.referredToEducation || "",
      referredToDiabetesPrevention: data?.referredToDiabetesPrevention || "",
      referralDetails: data?.referralDetails || "",
    },
  });

  const influenzaVaccineValue = form.watch("influenzaVaccine");
  const pneumococcalVaccineValue = form.watch("pneumococcalVaccine");
  const covidVaccineValue = form.watch("covidVaccine");
  const isInfluenzaDateDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(influenzaVaccineValue);
  const isPneumococcalDateDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(pneumococcalVaccineValue);
  const isCovidStatusDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(covidVaccineValue);

  const isReferralSelected =
    form.watch("referredToEducation") === "YES" ||
    form.watch("referredToDiabetesPrevention") === "YES";

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PlanAndOutcomeForm"))}
      isReview={isReview}
      isLastStep={true}
      isLoading={isLaoding}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PlanAndOutcomeForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Vaccinations</h3>
              <div
                className={style.inputGroupGrid2Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="influenzaVaccine">
                  <label className={style.addReferralFormLabel}>
                    Influenza Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={influenzaVaccineValue}
                    onChange={(value) => {
                      form.setValue("influenzaVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("influenzaVaccineDate", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                </FormField>
                <FormField name="influenzaVaccineDate">
                  <label className={style.addReferralFormLabel}>
                    Influenza Vaccine Date
                  </label>
                  <DatePickerField
                    value={ddmmyyyyToIso(form.watch("influenzaVaccineDate"))}
                    onChange={(isoDate) =>
                      form.setValue(
                        "influenzaVaccineDate",
                        isoToDdmmyyyy(isoDate),
                        { shouldValidate: true }
                      )
                    }
                    disabled={isInfluenzaDateDisabled}
                    maxDate={format(new Date(), "yyyy-MM-dd")}
                  />
                </FormField>
                <FormField name="pneumococcalVaccine">
                  <label className={style.addReferralFormLabel}>
                    Pneumococcal Polysaccharide Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={pneumococcalVaccineValue}
                    onChange={(value) => {
                      form.setValue("pneumococcalVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("pneumococcalVaccineDate", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                </FormField>
                <FormField name="pneumococcalVaccineDate">
                  <label className={style.addReferralFormLabel}>
                    Pneumococcal Polysaccharide Vaccine Date
                  </label>
                  <DatePickerField
                    value={ddmmyyyyToIso(form.watch("pneumococcalVaccineDate"))}
                    onChange={(isoDate) =>
                      form.setValue(
                        "pneumococcalVaccineDate",
                        isoToDdmmyyyy(isoDate),
                        { shouldValidate: true }
                      )
                    }
                    disabled={isPneumococcalDateDisabled}
                    maxDate={format(new Date(), "yyyy-MM-dd")}
                  />
                </FormField>
                <FormField name="covidVaccine">
                  <label className={style.addReferralFormLabel}>
                    COVID Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={covidVaccineValue}
                    onChange={(value) => {
                      form.setValue("covidVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("covidVaccineStatusUpToDate", "NO", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                  {/* GP prompt (client feedback): the patient's Covid vaccinations
                      may have been given elsewhere since the last recorded date, so
                      prompt the GP to confirm rather than assume the file is complete. */}
                  <p className={style.gpPrompt}>
                    {COVID_VACCINE_ELSEWHERE_PROMPT}
                  </p>
                </FormField>
                <FormField name="covidVaccineStatusUpToDate">
                  <label className={style.addReferralFormLabel}>
                    COVID Vaccine Status Up To Date
                  </label>
                  <Select
                    options={YES_NO_PATIENT_DECLINED_OPTIONS}
                    placeholder="Select"
                    value={form.watch("covidVaccineStatusUpToDate")}
                    onChange={(value) =>
                      form.setValue("covidVaccineStatusUpToDate", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isCovidStatusDisabled}
                    required
                  />
                </FormField>
              </div>
            </div>
            <div className={style.itemWrapper}>
              <h3>Prevention Programme Outcome</h3>

              <div
                className={style.inputGroupGrid2Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="diagnosedWithChronicDisease">
                  <label className={style.addReferralFormLabel}>
                    Diagnosed with Chronic Disease
                  </label>
                  <Select
                    disabled={isReview}
                    options={[
                      {
                        label: "Yes",
                        value: "YES",
                      },
                      {
                        label: "No",
                        value: "NO",
                      },
                    ]}
                    value={form.watch("diagnosedWithChronicDisease")}
                    onChange={(value) =>
                      form.setValue("diagnosedWithChronicDisease", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
              </div>

              {form.watch("diagnosedWithChronicDisease") === "YES" && (
                <>
                  <fieldset className={style.medsCheckboxGroup}>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("diabetesType2")}
                      />
                      Diabetes Type 2
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("asthma")}
                      />
                      Asthma
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("copd")}
                      />
                      COPD
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("heartFailure")}
                      />
                      Heart Failure
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("atrialFibrillation")}
                      />
                      Atrial Fibrillation
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("ischaemicHeartDisease")}
                      />
                      Ischaemic Heart Disease
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("stroke")}
                      />
                      Cerebrovascular Disease Stroke
                    </label>
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        disabled={isReview}
                        {...form.register("tia")}
                      />
                      Cerebrovascular Disease TIA
                    </label>
                  </fieldset>
                  {form.formState.errors.diagnosedWithChronicDisease && (
                    <p
                      style={{
                        color: "red",
                        fontSize: "14px",
                        marginTop: "8px",
                      }}
                    >
                      {
                        form.formState.errors.diagnosedWithChronicDisease
                          .message
                      }
                    </p>
                  )}
                </>
              )}
            </div>

            <div className={style.sourcesOfInformation}>
              <h4>
                Sources of information or support to help manage my long term
                condition
              </h4>
              <a
                href={ensureAbsoluteUrl("www.hse.ie/diabetes")}
                target="_blank"
                rel="noreferrer noopener"
              >
                www.hse.ie/diabetes
              </a>
              <a
                href={ensureAbsoluteUrl(
                  "www2.hse.ie/mental-and-physical-wellbeing/"
                )}
                target="_blank"
                rel="noreferrer noopener"
              >
                www2.hse.ie/mental-and-physical-wellbeing/
              </a>
            </div>
            <h3>Patient Education</h3>
            <div
              className={style.inputGroupGrid2Columns}
              style={{ marginTop: "16px" }}
            >
              <FormField name="educationProvided">
                <label className={style.addReferralFormLabel}>
                  Education Provided
                </label>
                <Select
                  placeholder="Select"
                  options={EDUCATION_PROVIDED_OPTIONS}
                  value={form.watch("educationProvided")}
                  onChange={(value) =>
                    form.setValue("educationProvided", value, {
                      shouldValidate: true,
                    })
                  }
                  disabled={isReview}
                  required
                />
              </FormField>
              <FormField name="referredToEducation">
                <label className={style.addReferralFormLabel}>
                  Referred to Education
                </label>
                <Select
                  placeholder="Select"
                  options={REFERRED_TO_EDUCATION_OPTIONS}
                  value={form.watch("referredToEducation")}
                  onChange={(value) =>
                    form.setValue("referredToEducation", value, {
                      shouldValidate: true,
                    })
                  }
                  disabled={isReview}
                  required
                />
              </FormField>
              {!!isRegisteredForPreDiabetes && (
                <FormField name="referredToDiabetesPrevention">
                  <label className={style.addReferralFormLabel}>
                    Referred to Diabetes Prevention
                  </label>
                  <Select
                    placeholder="Select"
                    options={REFERRED_TO_DIABETES_PREVENTION_OPTIONS}
                    value={form.watch("referredToDiabetesPrevention")}
                    onChange={(value) =>
                      form.setValue("referredToDiabetesPrevention", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                </FormField>
              )}
              {!!isReferralSelected && (
                <FormField name="referralDetails">
                  <label className={style.addReferralFormLabel}>
                    Referral Details
                  </label>
                  <Select
                    placeholder="Select"
                    options={REFERRAL_DETAILS_OPTIONS}
                    value={form.watch("referralDetails")}
                    onChange={(value) =>
                      form.setValue("referralDetails", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                </FormField>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
